// src/components/IdleTimeout.js
//
// Monitors user activity (mouse, keyboard, touch, scroll).
// After IDLE_WARNING_MS of inactivity, shows a warning dialog.
// After IDLE_LOGOUT_MS of total inactivity, forces logout.
// User can dismiss the warning to reset the timer.
//
// Usage: Place inside AuthProvider, renders only for authenticated users.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

// --- Configuration ---
const IDLE_WARNING_MS = 30 * 60 * 1000;   // 1 minute (TESTING — restore to 30 * 60 * 1000 for production)
const IDLE_LOGOUT_MS  = 45 * 60 * 1000;   // 2 minutes (TESTING — restore to 45 * 60 * 1000 for production)
const THROTTLE_MS     = 60 * 1000;         // 5 seconds (TESTING — restore to 60 * 1000 for production)

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];

export default function IdleTimeout() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);

  // Use refs to avoid dependency cycles with useEffect
  const logoutRef = useRef(logout);
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const throttleRef = useRef(0);
  const showWarningRef = useRef(false);

  // Keep refs current
  useEffect(() => { logoutRef.current = logout; }, [logout]);
  useEffect(() => { showWarningRef.current = showWarning; }, [showWarning]);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
  }, []);

  const resetTimers = useCallback(() => {
    clearTimers();
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      console.log('[IdleTimeout] Showing warning dialog.');
      setShowWarning(true);
    }, IDLE_WARNING_MS);

    logoutTimerRef.current = setTimeout(() => {
      console.log('[IdleTimeout] Session expired due to inactivity.');
      logoutRef.current();
    }, IDLE_LOGOUT_MS);
  }, [clearTimers]);

  // Set up event listeners — runs once when user is authenticated
  useEffect(() => {
    if (!user) return;

    console.log('[IdleTimeout] Initialized. Warning at', IDLE_WARNING_MS / 1000, 's, logout at', IDLE_LOGOUT_MS / 1000, 's');
    resetTimers();

    const handleActivity = () => {
      const now = Date.now();
      // Don't reset if warning is showing or throttle hasn't elapsed
      if (showWarningRef.current) return;
      if (now - throttleRef.current < THROTTLE_MS) return;
      throttleRef.current = now;
      resetTimers();
    };

    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    return () => {
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, handleActivity)
      );
      clearTimers();
    };
  }, [user, resetTimers, clearTimers]); // resetTimers and clearTimers are stable (no external deps)

  const handleDismissWarning = () => {
    console.log('[IdleTimeout] Warning dismissed by user.');
    resetTimers();
  };

  if (!user) return null;

  const minutesRemaining = Math.max(
    0,
    Math.ceil((IDLE_LOGOUT_MS - IDLE_WARNING_MS) / 60000)
  );

  return (
    <Dialog
      open={showWarning}
      onClose={handleDismissWarning}
      aria-labelledby="idle-timeout-dialog"
    >
      <DialogTitle id="idle-timeout-dialog">Session Timeout Warning</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You've been inactive for 30 minutes. Your session will automatically
          end in {minutesRemaining} minutes for security purposes. Click below
          to continue working.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDismissWarning} variant="contained" color="primary">
          I'm Still Here
        </Button>
      </DialogActions>
    </Dialog>
  );
}
